import {gql, useQuery} from '@apollo/client';
import {ValidatedTextInputField} from 'common';
import {useDebounce} from 'common/hook';
import {Pane, Popover, Position, TextInputField} from 'evergreen-ui';
import React from 'react';
import {useFormContext} from 'react-hook-form';
import {UserFormInput} from '../types';
import {useCombobox} from 'downshift';

type Provider = {
    id: string;
    guid: string;
    name: string;
};
type ProvidersResult = {
    providers: Provider[];
};

export const PROVIDERS_QUERY = gql`
query providerForUm($name: String) {
  providers(name: $name) {
    id
    guid
    name
  }
}
`;

export const AutocompleteProviderInput = () => {
    const [filerVal, setFilterVal] = React.useState("");
    const debounceValue = useDebounce(filerVal, 300);
    const {
        setValue,
        formState: {
            errors,
        },
    } = useFormContext();
    const [inputItems, setInputItems] = React.useState<Array<Provider>>([]);
    const inputRef = React.useRef<HTMLInputElement>(null);

    const {loading} = useQuery<ProvidersResult, {}>(PROVIDERS_QUERY, {
        variables: {
            name: debounceValue,
        },
        onCompleted: (data: ProvidersResult) => {
            if (data && data.providers) {
                setInputItems(data.providers)
            }
            inputRef.current?.focus();
        },
        fetchPolicy: 'cache-first',
        skip: debounceValue.length < 2,
    });


    const {
        isOpen,
        setInputValue,
        selectedItem,
        getMenuProps,
        getInputProps,
        getComboboxProps,
        highlightedIndex,
        getItemProps,
    } = useCombobox({
        items: inputItems,
        itemToString: (item) => item.name,
        onInputValueChange: ({inputValue}) => {
            setFilterVal(inputValue ?? "");
        },
        onSelectedItemChange: ({selectedItem}) => {
            if (selectedItem) {
                setValue("providerGuid", selectedItem.guid, {shouldValidate: true});
            }
        },
    })


    return (
        <Pane>
            <Popover
                isShown={isOpen}
                position={Position.BOTTOM_LEFT}
                onClose={() => {
                    // use for show current selected item, if input value empty clear input value
                    if (selectedItem && filerVal !== "" && inputItems.length !== 0) {
                        setInputValue(selectedItem.name);
                    } else {
                        setInputValue("");
                        setValue("providerGuid", null);
                    }
                }}
                content={
                    <Pane width={320}>
                        {isOpen && loading &&
                            <Pane paddingY={4} paddingLeft={15} className="loading-text">Loading</Pane>}
                        {isOpen &&
                            inputItems.map((item, index) => (
                                <Pane
                                    paddingY={4}
                                    whiteSpace={"nowrap"}
                                    paddingLeft={15}
                                    overflow={'hidden'}
                                    textOverflow={'ellipsis'}
                                    backgroundColor={highlightedIndex === index ? '#bde4ff' :
                                        selectedItem && selectedItem.guid === item.guid ? '#a0aec0' : '#fff'}
                                    key={`${item.guid}${index}`}
                                    {...getItemProps({item, index})}
                                >
                                    {item.name}
                                </Pane>
                            ))}
                        {isOpen && !loading && inputItems.length === 0 &&
                            <Pane paddingY={4} paddingLeft={15}>Not found</Pane>}
                    </Pane>
                }

                {...getMenuProps()}
            >
                <Pane>
                    <div {...getComboboxProps()}>
                        <TextInputField
                            label={<span className='text-base font-normal'>Provider</span>}
                            inputHeight={40}
                            inputWidth={320}
                            size={500}
                            placeholder="Provider"
                            isInvalid={!!errors.providerGuid}
                            data-testid='um-autocomplete-provider-input'
                            className={loading ? 'loading-cursor' : ''}
                            {...getInputProps({
                                ref: inputRef,
                                onChange: (e: any) => {
                                    if (e.target && e.target.value === "") {
                                        setInputValue("");
                                        setValue("providerGuid", null);
                                    }
                                },
                            })}
                        />
                    </div>
                </Pane>
            </Popover>
            <ValidatedTextInputField<UserFormInput>
                label={""}
                className='ev-overwrite ev-input-hidden'
                width={320}
                inputHeight={40}
                size={500}
                marginTop='-10px'
                name='providerGuid'
                data-testid='um-autocomplete-provider-input-guid'
                validationRules={{
                    required: 'This field is required'
                }}
            />
        </Pane>
    );
}
